import {useEffect} from 'react';
import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import {withState} from 'shared/core';
import useModal from 'shared/hooks/useModal';
import {Button, Layout, Panel} from 'shared/components';
import DragAndDropContext from 'shared/components/dnd/DragAndDropContext';
import {Shift} from 'main/stores/time_tracking';
import ShiftSchedulerState from 'main/containers/time_tracking/components/shifts/state/ShiftSchedulerState';
import ShiftGrid from './ShiftGrid';
import ScheduleLegend from './ScheduleLegend';
import EditShiftModal from './EditShiftModal';
import CopyEmployeeShiftsModal from './CopyEmployeeShiftsModal';
import RepeatScopeModal from './RepeatScopeModal';
import PublishConfirmationModal from './PublishConfirmationModal';
import PublishedShiftChangeModal from './PublishedShiftChangeModal';

const WeekNavigation = observer(({uiState}) => {
  const {weekRange} = uiState;

  return (
    <>
      <div className='flex align-items-middle justify-content-between w-full sm:w-auto'>
        <Button size='sm' trait='default' onClick={() => uiState.goToPreviousWeek()}>
          <i className='material-symbols-solid align-middle'>{'chevron_left'}</i>
        </Button>
        <div className='h3 medium px2 whitespace-nowrap'>{weekRange}</div>
        <Button size='sm' trait='default' onClick={() => uiState.goToNextWeek()}>
          <i className='material-symbols-solid align-middle'>{'chevron_right'}</i>
        </Button>
      </div>
      <Button className='grow sm:grow-0' size='sm' trait='default' onClick={() => uiState.goToCurrentWeek()}>
        <FormattedMessage id='time_tracking.shifts.This Week'/>
      </Button>
    </>
  );
});

const PublishScheduleButton = observer(({uiState}) => {
  const {schedule, isUpdating} = uiState;
  const publishableCount = schedule.unpublishedShifts.length;
  if (!schedule.hasLink('publish')) return null;

  return (
    <Button className='grow sm:grow-0' size='sm' disabled={publishableCount === 0 || isUpdating} onClick={() => uiState.requestPublish()}>
      <FormattedMessage id='time_tracking.shifts.PUBLISH_SCHEDULE' values={{count: publishableCount}}/>
    </Button>
  );
});

const SchedulerActions = observer(({uiState}) => {
  const {isUpdating} = uiState;

  return (
    <>
      <Button className='grow sm:grow-0 sm:ml-auto' size='sm' trait='default' disabled={isUpdating} onClick={() => uiState.copyPreviousWeek()}>
        <FormattedMessage id='time_tracking.shifts.Copy Previous Week'/>
      </Button>
      <PublishScheduleButton uiState={uiState}/>
    </>
  );
});

const useCopyModifierTracking = (uiState) => {
  useEffect(() => {
    // macOS browsers report Command on the mousedown and dragstart that begin a drag, but not reliably on
    // the dragover events during it, so once Command is seen it is latched for the rest of the drag.
    let commandHeld = false;

    const updateCopyOnDrop = e => {
      commandHeld = commandHeld || e.metaKey;
      uiState.copyOnDrop = e.altKey || e.ctrlKey || commandHeld;
    };
    const handleMouseDown = e => {
      commandHeld = e.metaKey;
    };
    const handleDragOver = e => {
      updateCopyOnDrop(e);
      if (!e.target.closest('.ShiftGrid-cell')) uiState.hoveredCell = null;
    };

    window.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('dragstart', updateCopyOnDrop, true);
    window.addEventListener('dragover', handleDragOver, true);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('dragstart', updateCopyOnDrop, true);
      window.removeEventListener('dragover', handleDragOver, true);
    };
  }, [uiState]);
};

const ShiftScheduler = observer(({uiState}) => {
  const editShiftModalOpen = useModal(uiState, 'EditShiftModal', 'editingShift', Shift, 'errors');
  const repeatScopeModalOpen = useModal(uiState, 'RepeatScopeModal', 'repeatScopeRequest');
  const copyEmployeeShiftsModalOpen = useModal(uiState, 'CopyEmployeeShiftsModal', 'copyingFromEmployee', null, 'copyErrors');
  const publishConfirmationModalOpen = useModal(uiState, 'PublishConfirmationModal', 'publishRequest');
  const publishedShiftChangeModalOpen = useModal(uiState, 'PublishedShiftChangeModal', 'publishedShiftChangeRequest');

  const {copyingFromEmployee, copyingFromEmployeeShifts, copyableEmployees, weekRange, copyErrors} = uiState;

  useCopyModifierTracking(uiState);

  return (
    <Layout>
      <Panel>
        <div className='PanelHeader flex flex-wrap align-items-middle gap-2 row-gap-1'>
          <WeekNavigation uiState={uiState}/>
          <SchedulerActions uiState={uiState}/>
        </div>
        <ScheduleLegend/>
        <ShiftGrid uiState={uiState}/>
      </Panel>
      <EditShiftModal uiState={uiState} isOpen={editShiftModalOpen}/>
      <RepeatScopeModal uiState={uiState} isOpen={repeatScopeModalOpen}/>
      <CopyEmployeeShiftsModal
        isOpen={copyEmployeeShiftsModalOpen}
        employee={copyingFromEmployee}
        shifts={copyingFromEmployeeShifts}
        employees={copyableEmployees}
        week={weekRange}
        errors={copyErrors}
        onCopy={targetEmployees => uiState.copyEmployeeShifts(targetEmployees)}
        onClose={() => uiState.closeCopyEmployeeShiftsModal()}
      />
      <PublishConfirmationModal uiState={uiState} isOpen={publishConfirmationModalOpen}/>
      <PublishedShiftChangeModal uiState={uiState} isOpen={publishedShiftChangeModalOpen}/>
    </Layout>
  );
});

export default withState(DragAndDropContext(ShiftScheduler), ShiftSchedulerState);
